import * as dates from "@client/dates";
import {useAsync} from "@client/hooks";
import {classNames} from "@reactivated";
import Compressor from "compressorjs";
import Decimal from "decimal.js";
import {includes} from "lodash-es";
import React from "react";

export const classes = (
    ...names: (
        | string
        | false
        | undefined
        | null
        | {[className: string]: boolean | undefined}
    )[]
): string => {
    return classNames(
        ...names.map((c) =>
            c != null && typeof c === "object"
                ? Object.keys(c)
                      .filter((key) => c[key] === true)
                      .join(" ")
                : c,
        ),
    );
};

export function assign<T extends U, U>(target: T, source: U): T & U;
export function assign<T extends U, U extends V, V>(
    target: T,
    source: U,
    source2: V,
): T & U & V;
export function assign<T extends U, U extends V, V extends W, W>(
    target: T,
    source: U,
    source2: V,
    source3: W,
): T & U & V & W;
// oxlint-disable-next-line typescript/no-explicit-any
export function assign(...args: any[]) {
    // oxlint-disable-next-line typescript/no-unsafe-return, typescript/no-unsafe-argument
    return Object.assign({}, ...args);
}

export const formatDistanceToNow = (date: DateTimeValue) =>
    dates.formatDistanceToNow(dates.parseISO(date));

export const formatDate = (date: DateValue) =>
    dates.format(dates.parseISO(date), "eee, MMM do yyyy");

export const formatDay = (date: DateValue) =>
    dates.format(dates.parseISO(date), "MMMM do");

export const formatShortMonthAndDay = (date: DateValue) =>
    dates.format(dates.parseISO(date), "M/d");

export const formatDecimalAndUnit = (
    value: Decimal | string | null,
    unit: "length" | "weight" | "IU" | "mg" | "percent" | "g" | "cal",
    isImperial: boolean,
) => {
    const decimalPlaces = 0;
    const empty = "-";

    if (value == null) {
        return empty;
    }

    const casted =
        typeof value == "string"
            ? new Decimal(value).toDecimalPlaces(decimalPlaces)
            : value;

    const suffix = (() => {
        if (unit === "percent") {
            return "%";
        } else if (unit === "IU") {
            return "IU";
        } else if (unit === "mg") {
            return "mg";
        } else if (unit === "g") {
            return "g";
        } else if (unit === "cal") {
            return "cal";
        } else if (unit === "length") {
            if (isImperial) {
                return "in";
            }
            return "cm";
        } else {
            if (isImperial) {
                return "lb";
            }
            return "kg";
        }
    })();
    return `${casted.toString()} ${suffix}`;
};

export const formatCurrency = (input: string | number, cents = true) => {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: cents == true ? undefined : 0,
        maximumFractionDigits: cents == true ? undefined : 0,
    }).format(typeof input == "string" ? parseFloat(input) : input);
};

export const lbOrKg = (
    weight: DecimalValue | null | undefined,
    isImperial: boolean,
) => {
    if (weight == null) {
        return "";
    }
    const weightKg = new Decimal(weight).dividedBy(isImperial ? "0.45359237" : "1");
    return isImperial
        ? weightKg.toDecimalPlaces(1).toString() + " lb"
        : weightKg.toDecimalPlaces(1).toString() + " kg";
};

export const filterPredicate = (haystack: string, needle: string) =>
    includes(haystack.toLowerCase(), needle.toLowerCase());

// Exact for arbitrary precision — parseFloat would round past ~15 significant
// digits. Unparseable input displays as-is rather than mangled.
export const removeTrailingZeros = (value: DecimalValue) => {
    try {
        return new Decimal(value).toString();
    } catch {
        return value;
    }
};

export const formatUnit = (unit: string) => {
    if (unit === "tablespoon") {
        return "tbsp";
    } else if (unit === "teaspoon") {
        return "tsp";
    }
    return unit;
};

export type DiscriminateUnion<T, K extends keyof T, V extends T[K]> =
    T extends Record<K, V> ? T : never;

// This always returns a URL. Even on web clients.
export const getAPIEndpoint = () => {
    return (
        (typeof localStorage === "undefined"
            ? null
            : localStorage.getItem("JOY_API_ENDPOINT")) ??
        process.env.JOY_API_ENDPOINT ??
        "https://www.joyapp.com"
    );
};

export const setAPIEndpoint = (endpoint: string) => {
    localStorage.setItem("JOY_API_ENDPOINT", endpoint);
};

// TODO: maybe this is a better approach. Set this once when the app loads.
// https://developer.mozilla.org/en-US/docs/Web/HTML/Element/base
export const getBaseURL = () => {
    if (typeof window === "undefined" || window.cordova == null) {
        return "";
    }
    return getAPIEndpoint();
};

// Similar to the above, but for referencing things outside capacitor, like
// Android's Stripe Checkout needing to reference a logo or the ShareBar
// everywhere needing to reference a full URL.
export const getBaseURLWithOriginForCapacitor = () => {
    return window.cordova == null ? window.location.origin : getBaseURL();
};

export const getStaticBucketURL = () => {
    const JOY_API_ENDPOINT = getAPIEndpoint();
    return (
        JOY_API_ENDPOINT.replace(/\./g, "-").replace(
            "www",
            "storage.googleapis.com/videos",
        ) + "/"
    );
};

export function generateCopyLabel(baseLabel: string, existingLabels: string[]) {
    let copyNumber = 1;
    let newLabel = `${baseLabel} (Copy ${copyNumber})`;

    while (existingLabels.includes(newLabel)) {
        copyNumber++;
        newLabel = `${baseLabel} (Copy ${copyNumber})`;
    }

    return newLabel;
}

export function roundNumberToNearest(decimalValue: Decimal, nearest: Decimal) {
    const roundedValue = decimalValue.dividedBy(nearest).round().times(nearest);
    if (roundedValue.isNaN()) {
        return new Decimal(0);
    }
    return roundedValue;
}

export function delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export function groupConsecutiveEqualElements<T>(
    arr: T[],
    compare: (left: T, right: T) => boolean,
): (T[] | T)[] {
    if (arr.length === 0) return [];

    const result: (T[] | T)[] = [arr[0]];

    for (let i = 1; i < arr.length; i++) {
        if (compare(arr[i], arr[i - 1]) === true) {
            const pointer = result[result.length - 1];

            if (Array.isArray(pointer)) {
                pointer.push(arr[i]);
            } else {
                result[result.length - 1] = [result[result.length - 1], arr[i]] as
                    | T
                    | T[];
            }
        } else {
            result.push(arr[i]);
        }
    }

    return result;
}

export function convertSecondsToHoursMinutes(seconds: number) {
    // Calculate the number of hours
    const hours = Math.floor(seconds / 3600);

    // Calculate the remaining seconds after extracting hours
    const remainingSeconds = seconds % 3600;

    // Calculate the number of minutes
    const minutes = Math.floor(remainingSeconds / 60);

    // Return the result as an object or string
    if (minutes == 0) {
        return `${hours} hr`;
    }
    return `${hours} hr, ${minutes} min`;
}

export function cloudflareImageURL(
    imageUID: string,
    variant: "public" | "thumbnail" = "public",
) {
    return `https://imagedelivery.net/ELiLiOfYeVi79Put2yGLDg/${imageUID}/${variant}`;
}

export function useHMRAsync<T extends Promise<unknown>>(fn: () => T) {
    const initialized = React.useRef<T | null>(null);

    return useAsync(() => {
        if (initialized.current == null) {
            initialized.current = fn();
        }
        return initialized.current;
    }, []);
}

export function memoizeFirst<TArgs extends unknown[], TResult>(
    fn: (...args: TArgs) => TResult,
) {
    const {memoized} = new (class {
        outstanding: TResult | null = null;

        memoized = (...args: TArgs) => {
            if (this.outstanding == null) {
                this.outstanding = fn(...args);
            }
            return this.outstanding;
        };
    })();
    return memoized;
}

export function getFontSizeVW(text: string, avgInterGlyphWidth = 0.53) {
    if (text.length == 0) {
        return "1cqw";
    }
    if (text.length < 22) {
        return "1.5rem";
    }
    const size = 95 / (text.length * avgInterGlyphWidth);
    return `${size}cqw`;
}

export function formatAudioTime(seconds: number) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    // Use NumberFormat to ensure 2 digits with leading zeros
    const formatter = new Intl.NumberFormat("en-US", {
        minimumIntegerDigits: 2,
        useGrouping: false,
    });

    return `${formatter.format(minutes)}:${formatter.format(remainingSeconds)}`;
}

export function truncateUnit(unit: string, maxWidth: number) {
    // Truncate unit text if it would exceed maxWidth at text-xs Inter font
    // Approximate character width for Inter at text-xs (~0.75rem = 12px): ~6px per character
    const maxChars = Math.floor(maxWidth / 6);
    if (unit.length <= maxChars) return unit;
    return unit.substring(0, maxChars - 1) + "…";
}

export async function resizeImage(file: File, size: number) {
    return new Promise<Blob | null>((resolve) => {
        new Compressor(file, {
            quality: 0.6,
            maxWidth: size,
            success: (blob) => {
                resolve(blob);
            },
            error: () => {
                resolve(null);
            },
        });
    });
}

export const pluralize = (count: number, singular: string, plural?: string) => {
    return count === 1 ? singular : (plural ?? singular + "s");
};

export function withCleanup<TArgs extends unknown[], TReturn>(
    cleanup: () => void,
    fn: (...args: TArgs) => Promise<TReturn>,
): (...args: TArgs) => Promise<TReturn> {
    return async (...args: TArgs) => {
        try {
            return await fn(...args);
        } catch (error) {
            cleanup();
            throw error;
        }
    };
}
